import { createContext, useContext } from 'react';

import { DEFAULT_MAX_ADDITIONAL_DOCS_ALLOWED } from '@xing-com/crate-jobs-domain-apply-constants';
import type {
  InstantApplyJobDataType,
  InstantApplyJobXingApplicationDataType,
} from '@xing-com/crate-jobs-domain-apply-types';

export const InstantApplyJobDataContext = createContext<
  InstantApplyJobDataType | undefined
>(undefined);

export const useInstantApplyJobDataContext = (): InstantApplyJobDataType => {
  const instantApplyJobData = useContext(InstantApplyJobDataContext);

  if (instantApplyJobData === undefined) {
    throw new Error(
      'instantApplyJobData must be used within an InstantApplyJobDataContext'
    );
  }

  return instantApplyJobData;
};

export const useJobXingApplicationData =
  (): InstantApplyJobXingApplicationDataType => {
    const instantApplyJobData = useInstantApplyJobDataContext();

    return instantApplyJobData.application;
  };

export const useDisableAdditionalCommentsData = (): boolean => {
  const instantApplyJobData = useJobXingApplicationData();

  return instantApplyJobData.formSettings?.disableAdditionalComments ?? false;
};

export const useShowAdditionalDocsData = (): boolean => {
  const instantApplyJobData = useJobXingApplicationData();

  const allowedAdditionalDocs =
    instantApplyJobData.formSettings?.maxAdditionalDocuments ??
    DEFAULT_MAX_ADDITIONAL_DOCS_ALLOWED;

  return allowedAdditionalDocs >= DEFAULT_MAX_ADDITIONAL_DOCS_ALLOWED;
};

export const useFormSettingsTrackingFlag = (): string => {
  const { formSettings } = useJobXingApplicationData();

  return `max_additional_documents_${
    formSettings?.maxAdditionalDocuments ?? DEFAULT_MAX_ADDITIONAL_DOCS_ALLOWED
  },disable_comments_${formSettings?.disableAdditionalComments ?? false}`;
};

export const useJobGlobalId = (): string => {
  return useInstantApplyJobDataContext().globalId;
};

export const useJobCompanyName = (): string | undefined => {
  return useInstantApplyJobDataContext().companyInfo.companyNameOverride;
};
